import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { postProductFeedback, type ProductFeedbackPayload } from "../../lib/api";
import { readImageFile, type ReadImage } from "../../lib/screenshot";
import { getUser } from "../../lib/auth";
import { IconPaperclip, IconTrash } from "../icons";

interface FeedbackModalProps {
  onClose: () => void;
  /** Capture context (openspec §5.1): the chat session this was opened from. */
  conversationId?: string | null;
  responseId?: string | null;
}

type Category = ProductFeedbackPayload["category"];

const CATEGORIES: { value: Category; label: string }[] = [
  { value: "bug", label: "问题反馈" },
  { value: "feature", label: "功能建议" },
  { value: "usability", label: "体验问题" },
  { value: "other", label: "其他" }
];

/** Leading check icon revealed on the selected chip (same as AnswerActions). */
function ChipTick(): JSX.Element {
  return (
    <svg className="tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const MAX_TITLE = 120;
const MAX_DESC = 2000;

/**
 * Feedback about CycleCare itself (openspec/cyclecare-feedback-entry.md) — a
 * channel distinct from answer ratings and support tickets. Mirrors the
 * TicketsPanel modal shell + the Composer's image handling.
 */
export function FeedbackModal({ onClose, conversationId, responseId }: FeedbackModalProps): JSX.Element {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<Category>("bug");
  const [description, setDescription] = useState("");
  const [shot, setShot] = useState<ReadImage | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ number?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

  // Context captured silently with the feedback (mock §2 / openspec §5.1) so a
  // reviewer understands it without leaving the console. conversationId may be null.
  const user = getUser();
  const page = window.location.pathname;

  const pickFile = async (file: File | undefined): Promise<void> => {
    if (file === undefined) return;
    const img = await readImageFile(file);
    if (img === null) {
      setError("截图需为 png/jpeg/gif/webp 且小于 3MB");
      return;
    }
    setError(null);
    setShot(img);
  };

  const submit = async (): Promise<void> => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const res = await postProductFeedback({
      title: title.trim(),
      category,
      description: description.trim(),
      page: window.location.pathname,
      ...(conversationId ? { conversationId } : {}),
      ...(responseId ? { responseId } : {}),
      ...(shot !== null ? { screenshot: { data: shot.data, mimeType: shot.mimeType } } : {})
    });
    setSubmitting(false);
    if (res.ok) {
      setDone({ ...(res.number !== undefined ? { number: res.number } : {}) });
      window.setTimeout(onClose, 1800);
    } else {
      setError("提交失败,请稍后再试");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal feedback-modal"
        role="dialog"
        aria-modal="true"
        aria-label="意见反馈"
        tabIndex={-1}
        ref={modalRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div className="modal-title">意见反馈</div>
          <button className="rail-close" type="button" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </div>

        {done !== null ? (
          <div className="feedback-done">
            已收到,感谢反馈{done.number !== undefined ? ` · #${done.number}` : ""}。
          </div>
        ) : (
          <div className="feedback-form">
            <label className="feedback-field">
              <span className="feedback-label">标题</span>
              <input
                className="composer-input"
                type="text"
                value={title}
                maxLength={MAX_TITLE}
                placeholder="一句话概括你的反馈"
                onChange={(e) => setTitle(e.target.value)}
              />
            </label>

            <div className="feedback-field">
              <span className="feedback-label">类型</span>
              <div className="feedback-chips" role="group" aria-label="反馈类型">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    className="aa-chip"
                    aria-pressed={category === c.value}
                    onClick={() => setCategory(c.value)}
                  >
                    {c.label}
                    <ChipTick />
                  </button>
                ))}
              </div>
            </div>

            <label className="feedback-field">
              <span className="feedback-label">描述</span>
              <textarea
                className="composer-input feedback-textarea"
                value={description}
                maxLength={MAX_DESC}
                placeholder="说说发生了什么、你期望的结果…"
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="feedback-count">
                {description.length}/{MAX_DESC}
              </span>
            </label>

            <div className="feedback-field">
              <span className="feedback-label">截图(可选)</span>
              {shot === null ? (
                <button className="feedback-attach" type="button" onClick={() => fileRef.current?.click()}>
                  <IconPaperclip size={14} /> 添加截图
                </button>
              ) : (
                <div className="feedback-shot">
                  <img src={shot.dataUrl} alt="截图预览" />
                  <button className="feedback-shot-del" type="button" aria-label="移除截图" onClick={() => setShot(null)}>
                    <IconTrash size={14} />
                  </button>
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/gif,image/webp"
                hidden
                onChange={(e) => void pickFile(e.target.files?.[0])}
              />
            </div>

            <div className="feedback-field">
              <span className="feedback-label">随反馈附带的上下文(自动采集)</span>
              <div className="feedback-context">
                <div className="kv">
                  <b>会话</b>
                  <span>{conversationId ?? "无(非对话内提交)"}</span>
                </div>
                <div className="kv">
                  <b>所在页面</b>
                  <span>{page}</span>
                </div>
                <div className="kv">
                  <b>账号 / 租户</b>
                  <span>{user !== null ? `${user.username} · ${user.tenant}` : "—"}</span>
                </div>
              </div>
            </div>

            {error !== null && <div className="feedback-error">{error}</div>}

            <div className="feedback-actions">
              <button className="feedback-cancel" type="button" onClick={onClose}>
                取消
              </button>
              <button className="feedback-submit" type="button" disabled={!canSubmit} onClick={() => void submit()}>
                {submitting ? "提交中…" : "提交"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
